/**
 * Generic GLSL helper functions.
 * @module
 */
import type * as THREE from "three";

const USE_ROBUST_BASIS_HELPER = true;

// for comparative testing
const referenceBasisHelperDeclaration = `
mat3 basis(in vec2 vUv, in vec3 pos, in vec3 norm) {
    vec3 tg1 = normalize(cross(norm, vec3(1,0,0)));
    vec3 btg = normalize(cross(norm, tg1));
    vec3 tgt = normalize(cross(norm, btg));
    return mat3( tgt, btg, normalize( norm ) );
}
`;

// Done per-component to fix potential bugs on some devices
const robustBasisHelperDeclaration = `
mat3 basis(in vec2 uv,	in vec3 pos, in vec3 norm) {
    vec3 dposX = vec3( dFdx( pos.x ), dFdx( pos.y ), dFdx( pos.z ) );
    vec3 dposY = vec3( dFdy( pos.x ), dFdy( pos.y ), dFdy( pos.z ) );
    vec2 duvX = vec2( dFdx( uv.x ), dFdx( uv.y ) );
    vec2 duvY = vec2( dFdy( uv.x ), dFdy( uv.y ) );
    float direction = sign( duvY.y*duvX.x - duvX.y*duvY.x );
    vec3 tgt = normalize( ( dposX*duvY.y - dposY*duvX.y )*direction );
    vec3 btg = normalize( ( -dposX*duvY.x + dposY*duvX.x )*direction );
    return mat3( tgt, btg, normalize( norm ) );
}
`;

export const basisHelperDeclaration = USE_ROBUST_BASIS_HELPER
  ? robustBasisHelperDeclaration
  : referenceBasisHelperDeclaration;

// Similar to perturbNormal2Arb definition in three.js upstream, included because perturbNormal2Arb is behind #ifdefs.
export const dmPerturbNormalDeclaration = `
vec3 dmPerturbNormal( vec3 eye_pos, vec3 surf_norm, vec3 normData ) {
    vec3 q0 = vec3( dFdx( eye_pos.x ), dFdx( eye_pos.y ), dFdx( eye_pos.z ) );
    vec3 q1 = vec3( dFdy( eye_pos.x ), dFdy( eye_pos.y ), dFdy( eye_pos.z ) );
    vec2 st0 = dFdx( vUv.st );
    vec2 st1 = dFdy( vUv.st );
    float scale = sign( st1.t * st0.s - st0.t * st1.s ); // we do not care about the magnitude
    vec3 S = normalize( ( q0 * st1.t - q1 * st0.t ) * scale );
    vec3 T = normalize( ( - q0 * st1.s + q1 * st0.s ) * scale );
    vec3 N = normalize( surf_norm );
    mat3 tsn = mat3( S, T, N );
    return normalize( tsn * normData );
}
`;

export const arcVisibilityUniformWeightDeclaration = `
float arcVisibilityUniformWeight( const in float horizonAngle1, const in float horizonAngle2 ) {
    return 2.0 - cos(horizonAngle1) - cos(horizonAngle2);
}
`;

export const hash21Declaration = `
#ifndef HIGHARC_HASH21_HELPER
#define HIGHARC_HASH21_HELPER
float hash21(vec2 p)
{
    vec3 q  = fract(vec3(p.xyx) * .4123);
    q += dot(q.yzx + 23.13, q);
    return fract(q.z*fract(q.x + q.y));
}
#endif
`;

export function channelMaskUniform(selectedChannel: number): THREE.IUniform {
  const channelMask = new Float32Array(4);
  channelMask[selectedChannel] = 1;
  return { value: channelMask };
}

export const OUTSIDE_RANDOM_MASK_VALUE = -500.0;
